import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Quote = Tables<'quotes'>
type QuoteLineItem = Tables<'quote_line_items'>

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [quote, setQuote] = useState<Quote | null>(null)
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [showConvert, setShowConvert] = useState(false)
  const [deliveryDate, setDeliveryDate] = useState('')
  const [poNumber, setPoNumber] = useState('')

  useEffect(() => {
    if (!id) return
    async function load() {
      const [quoteRes, linesRes] = await Promise.all([
        supabase.from('quotes').select('*').eq('id', id!).single(),
        supabase.from('quote_line_items').select('*').eq('quote_id', id!).order('line_number'),
      ])
      setQuote(quoteRes.data)
      setLineItems(linesRes.data || [])
      setLoading(false)
    }
    load()
  }, [id])

  const updateStatus = async (status: string) => {
    if (!quote || !profile) return
    setActionLoading(true)
    const updates: Record<string, unknown> = { status }
    if (status === 'sent') updates.sent_at = new Date().toISOString()
    if (status === 'accepted') updates.accepted_at = new Date().toISOString()
    if (status === 'rejected') updates.rejected_at = new Date().toISOString()

    await supabase.from('quotes').update(updates).eq('id', quote.id)
    await supabase.from('activity_log').insert({
      organization_id: quote.organization_id,
      project_id: quote.project_id,
      actor_id: profile.id,
      activity_type: `quote_${status}` as 'quote_sent' | 'quote_accepted' | 'quote_rejected',
      entity_type: 'quote',
      entity_id: quote.id,
      summary: `Quote ${quote.reference_code} marked as ${status}`,
    })
    setQuote({ ...quote, ...updates } as Quote)
    setActionLoading(false)
  }

  const handleConvertToOrder = async () => {
    if (!quote) return
    setActionLoading(true)
    const { data: orderId, error } = await supabase.rpc('convert_quote_to_order', {
      p_quote_id: quote.id,
      p_requested_delivery: deliveryDate || undefined,
      p_po_number: poNumber || undefined,
    })
    if (error) {
      alert('Error: ' + error.message)
      setActionLoading(false)
      return
    }
    navigate(`/gaviota/orders/${orderId}`)
  }

  if (loading) return <p style={{ color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>Loading quote...</p>
  if (!quote) return <p>Quote not found</p>

  const canEdit = ['draft'].includes(quote.status) && profile && ['sales_consultant', 'management', 'partner_admin', 'keder_admin'].includes(profile.role)
  const canSend = ['draft'].includes(quote.status)
  const canAcceptReject = ['sent'].includes(quote.status)
  const canConvert = ['accepted', 'sent', 'draft'].includes(quote.status)

  return (
    <div>
      <PageHeader
        title={`Quote ${quote.reference_code}`}
        description={`Version ${quote.version} · Created ${new Date(quote.created_at).toLocaleDateString()}`}
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            {canSend && <button className={css.btnSecondary} onClick={() => updateStatus('sent')} disabled={actionLoading}>Mark as Sent</button>}
            {canAcceptReject && <button className={css.btnPrimary} onClick={() => updateStatus('accepted')} disabled={actionLoading}>Accept</button>}
            {canAcceptReject && <button className={css.btnSecondary} onClick={() => updateStatus('rejected')} disabled={actionLoading}>Reject</button>}
            {canConvert && <button className={css.btnPrimary} onClick={() => setShowConvert(true)} disabled={actionLoading}>Convert to Order</button>}
          </div>
        }
      />

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-6)' }}>
        <StatusBadge label={quote.status} />
        {quote.valid_until && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Valid until {new Date(quote.valid_until).toLocaleDateString()}</span>}
      </div>

      {showConvert && (
        <div className={css.modalOverlay} onClick={() => setShowConvert(false)}>
          <div className={css.modal} onClick={e => e.stopPropagation()}>
            <div className={css.modalHeader}><h2>Convert to Order</h2></div>
            <div className={css.modalBody}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>All line items and pricing will be carried forward. Add delivery details below.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Requested Delivery Date</label>
                  <input type="date" className={css.formInput} value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>PO Number (optional)</label>
                  <input className={css.formInput} value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="Customer PO reference" />
                </div>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowConvert(false)}>Cancel</button>
              <button className={css.btnPrimary} onClick={handleConvertToOrder} disabled={actionLoading}>{actionLoading ? 'Converting...' : 'Convert to Order'}</button>
            </div>
          </div>
        </div>
      )}

      <div className={css.card} style={{ marginBottom: 'var(--space-4)', padding: 0, overflow: 'hidden' }}>
        <table className={css.table}>
          <thead><tr><th>#</th><th>Description</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr></thead>
          <tbody>
            {lineItems.map(item => (
              <tr key={item.id}>
                <td>{item.line_number}</td>
                <td>{item.description || 'Product'}</td>
                <td>{item.quantity}</td>
                <td>£{Number(item.unit_price).toLocaleString()}</td>
                <td style={{ fontWeight: 500 }}>£{Number(item.line_total).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={css.card} style={{ maxWidth: 400, marginLeft: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Subtotal</span><span>£{Number(quote.subtotal).toLocaleString()}</span></div>
          {Number(quote.discount_amount) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Discount ({quote.discount_pct}%)</span><span>−£{Number(quote.discount_amount).toLocaleString()}</span></div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--text-lg)' }}>Total</span>
            <span style={{ fontWeight: 700, fontSize: 'var(--text-lg)', color: 'var(--color-primary-dark)' }}>£{Number(quote.total).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {(quote.customer_notes || quote.internal_notes) && (
        <div className={css.grid2} style={{ marginTop: 'var(--space-6)' }}>
          {quote.customer_notes && (
            <div className={css.card}>
              <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>Customer Notes</h3>
              <p style={{ fontSize: 'var(--text-sm)' }}>{quote.customer_notes}</p>
            </div>
          )}
          {quote.internal_notes && (
            <div className={css.card}>
              <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>Internal Notes</h3>
              <p style={{ fontSize: 'var(--text-sm)' }}>{quote.internal_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
